/**
 * Pre-battle supplies (MP-06, docs/SHOP.md §14) and farm twists (MP-25), parsed from the generated bundle: shop.json
 * `supplies` (the four items, their gem prices and what each does) and modes.json `farm` (the twist a replay of a cleared
 * level may take for more reward). The rules live in meta/supplies.ts and meta/farmTwist.ts; the sim reads only the run's
 * own copy of the numbers (sim/modifiers.ts SuppliesRule, FarmRule), never this data.
 */
import { Fix64 } from "../maths/fix64";

/** The four supplies, in the battle card's and the Shop's order. */
export const SUPPLY_IDS = ["warBanner", "scoutsMap", "supplyWagon", "rallyHorn"] as const;
export type SupplyId = (typeof SUPPLY_IDS)[number];
export const isSupplyId = (v: unknown): v is SupplyId => typeof v === "string" && (SUPPLY_IDS as readonly string[]).includes(v);

/** One supply: its name, gold price and its effect's number (`amount`; the Rally Horn also `waves`). */
export interface SupplyData {
  readonly id: SupplyId;
  readonly name: string;
  /** What it does, in a line (the Shop tile and the battle card's well). */
  readonly blurb: string;
  /**
   * Its gold price, in average Win chests of gold at the player's highest cleared level (orders.ts `chestShare`, like
   * the gold packs): owner 2026-10-23, supplies are bought for gold (they were 20–40 gems, poor value beside the boosts).
   */
  readonly goldChests: Fix64;
  /**
   * War Banner: extra checkpoint retries. Scout's Map: in-run hands shown ahead. Supply Wagon: reroll charges at the
   * start. Rally Horn: extra fortress troops (one squad) for the first `waves` waves.
   */
  readonly amount: number;
  /** Rally Horn: the waves its squad stays (0 for the others). */
  readonly waves: number;
}

export interface SuppliesData {
  /** In SUPPLY_IDS order; empty in an older bundle (no supplies anywhere). */
  readonly items: readonly SupplyData[];
  /** At most this many of each held (a grant past it is kept up to the cap). */
  readonly maxOwned: number;
  /** At most this many of each taken into one battle (the plan: 1). */
  readonly perBattle: number;
  /** The Shop's Supplies section and the battle card's wells open after this level is cleared. */
  readonly unlockAfterLevel: number;
}

export const NO_SUPPLIES: SuppliesData = { items: [], maxOwned: 0, perBattle: 0, unlockAfterLevel: Number.MAX_SAFE_INTEGER };

/** A farm twist (MP-25, D6): an optional Weekly Trial modifier on a replay of a cleared level, for more reward. */
export interface FarmTwistData {
  /** The battle chest's gold on a won twisted run × (1 + rewardBonus). */
  readonly rewardBonus: Fix64;
  /** Weekly Trial modifier ids (events.json trial.modifiers) a twist may be; the day's pick per level is one of them. */
  readonly modifiers: readonly string[];
  /** Twists are offered once this level is cleared … */
  readonly unlockAfterLevel: number;
  /** … on cleared levels from this one up. */
  readonly minLevel: number;
}

export const NO_FARM_TWIST: FarmTwistData = { rewardBonus: Fix64.Zero, modifiers: [], unlockAfterLevel: Number.MAX_SAFE_INTEGER, minLevel: 1 };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

function int(v: Json, where: string, min = 0): number {
  if (typeof v === "number" && Number.isInteger(v) && v >= min) return v;
  throw new TypeError(`${where}: expected an integer >= ${min}, got ${JSON.stringify(v)}`);
}
function chests(v: Json, where: string): Fix64 {
  const f = typeof v === "string" || typeof v === "number" ? Fix64.parse(String(v)) : null;
  if (f && f.isPositive()) return f;
  throw new TypeError(`${where}: expected a positive decimal, got ${JSON.stringify(v)}`);
}
function str(v: Json, where: string): string {
  if (typeof v === "string") return v;
  throw new TypeError(`${where}: expected a string`);
}

/** shop.json `supplies`; absent: none. Every id once, in SUPPLY_IDS order. */
export function parseSupplies(s: Json): SuppliesData {
  if (s === undefined || s === null) return NO_SUPPLIES;
  const list = s.items as Json[];
  if (!Array.isArray(list)) throw new Error("shop.supplies.items: a list");
  const items = list.map((x, i): SupplyData => {
    const w = `shop.supplies.items[${i}]`;
    const id = x?.id;
    if (!isSupplyId(id)) throw new Error(`${w}: unknown supply '${String(id)}'`);
    return { id, name: str(x.name, `${w}.name`), blurb: str(x.blurb ?? "", `${w}.blurb`), goldChests: chests(x.goldChests, `${w}.goldChests`), amount: int(x.amount, `${w}.amount`, 1), waves: int(x.waves ?? 0, `${w}.waves`) };
  });
  if (items.map((x) => x.id).join() !== SUPPLY_IDS.join()) throw new Error(`shop.supplies.items: exactly ${SUPPLY_IDS.join(", ")}, in that order`);
  return { items, maxOwned: int(s.maxOwned, "shop.supplies.maxOwned", 1), perBattle: int(s.perBattle, "shop.supplies.perBattle", 1), unlockAfterLevel: int(s.unlockAfterLevel ?? 0, "shop.supplies.unlockAfterLevel") };
}

/** modes.json `farm`; absent: no twists. */
export function parseFarmTwist(f: Json): FarmTwistData {
  if (f === undefined || f === null) return NO_FARM_TWIST;
  const bonus = Fix64.parse(String(f.rewardBonus));
  if (bonus.raw < 0n) throw new RangeError("modes.farm.rewardBonus: not negative");
  const mods = f.modifiers as Json[];
  if (!Array.isArray(mods) || mods.length === 0 || !mods.every((m) => typeof m === "string")) throw new Error("modes.farm.modifiers: Trial modifier ids");
  return { rewardBonus: bonus, modifiers: mods as string[], unlockAfterLevel: int(f.unlockAfterLevel, "modes.farm.unlockAfterLevel"), minLevel: int(f.minLevel, "modes.farm.minLevel", 1) };
}
